import { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectAuth } from "../auth/authSlice";
import { Role } from "../auth/types/authType";
import { RoleTypes } from "../auth/types/roleTypes";
import { isPrivilegedTransaction } from "../../utils/authUtils";
import { fetchPopupNotifications } from "../process/processSlices";
import { apolloClient } from "../../apollo/graphql";
import {
  REMOVE_PUSH_SUBSCRIPTION,
  SAVE_PUSH_SUBSCRIPTION,
} from "../../apollo/mutations";
import {
  getExistingPushSubscription,
  isPushSupported,
  subscribeToPush,
  subscriptionToInput,
  unsubscribeFromPush,
} from "./pushNotifications";

const POLL_INTERVAL_MS = 60_000;
const PUSH_PROMPT_KEY = "cvplus_push_prompt_dismissed";

export function useNotificationSetup(options?: { enablePolling?: boolean }) {
  const enablePolling = options?.enablePolling ?? false;
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(selectAuth);
  const [pushPermission, setPushPermission] = useState<NotificationPermission>(
    typeof Notification !== "undefined" ? Notification.permission : "default",
  );
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const prevCountRef = useRef(0);
  const prevIdsRef = useRef<Set<string>>(new Set());

  const canView =
    isPrivilegedTransaction(
      (user?.roles ?? []) as Role[],
      RoleTypes.ViewNotifications,
    ) ||
    isPrivilegedTransaction(
      (user?.roles ?? []) as Role[],
      RoleTypes.ViewWall,
    );

  useEffect(() => {
    if (!enablePolling || !canView || !user) return;

    const poll = () => {
      dispatch(fetchPopupNotifications());
    };

    poll();
    const interval = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [dispatch, canView, user?._id, enablePolling]);

  useEffect(() => {
    if (!canView) return;
    getExistingPushSubscription().then((sub) => {
      setPushSubscribed(!!sub);
      setPushPermission(Notification.permission);
    });
  }, [canView]);

  const enablePush = async (): Promise<boolean> => {
    const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
    if (!isPushSupported() || !vapidKey) return false;

    const subscription = await subscribeToPush(vapidKey);
    if (!subscription) {
      setPushPermission(Notification.permission);
      return false;
    }

    await apolloClient.mutate({
      mutation: SAVE_PUSH_SUBSCRIPTION,
      variables: subscriptionToInput(subscription),
    });
    setPushSubscribed(true);
    setPushPermission("granted");
    return true;
  };

  const disablePush = async (): Promise<void> => {
    const existing = await getExistingPushSubscription();
    if (existing) {
      await apolloClient.mutate({
        mutation: REMOVE_PUSH_SUBSCRIPTION,
        variables: { endpoint: existing.endpoint },
      });
      await unsubscribeFromPush();
    } else {
      await apolloClient.mutate({
        mutation: REMOVE_PUSH_SUBSCRIPTION,
        variables: { endpoint: null },
      });
    }
    setPushSubscribed(false);
  };

  const shouldShowPushPrompt =
    canView &&
    isPushSupported() &&
    pushPermission === "default" &&
    !pushSubscribed &&
    !localStorage.getItem(PUSH_PROMPT_KEY);

  const dismissPushPrompt = () => {
    localStorage.setItem(PUSH_PROMPT_KEY, "1");
  };

  return {
    canView,
    pushPermission,
    pushSubscribed,
    pushSupported: isPushSupported(),
    enablePush,
    disablePush,
    shouldShowPushPrompt,
    dismissPushPrompt,
    prevCountRef,
    prevIdsRef,
  };
}
